import { useEffect, useMemo, useState, useRef } from "react";
import { Pause, Play } from "lucide-react";
import { Modal } from "../../components/ui/modal";
import { Dropdown } from "../../components/ui/dropdown";
import { CORE } from "../../lib/api";
import { formatDuration } from "../../lib/time";
import { cn } from "../../lib/utils";
import type { CallRecord } from "./types";
import { TranscriptConversation } from "./transcript-conversation";
import { useRecordingUrl } from "./use-recording-url";

const SPEEDS = [
  { value: "0.5", label: "0.5×" },
  { value: "1", label: "1×" },
  { value: "1.5", label: "1.5×" },
  { value: "2", label: "2×" },
] as const;

/** Deterministic mock waveform bars from execution id. */
function bars(seed: string, n = 56): number[] {
  let h = 2166136261;
  for (const ch of seed) { h ^= ch.charCodeAt(0); h = Math.imul(h, 16777619); }
  const out: number[] = [];
  for (let i = 0; i < n; i++) {
    h = Math.imul(h ^ (h >>> 15), 2246822519);
    out.push(0.25 + (((h >>> 8) % 1000) / 1000) * 0.75);
  }
  return out;
}

export function RecordingModal({ call, onClose }: { call: CallRecord; onClose: () => void }) {
  const [playing, setPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [speed, setSpeed] = useState<"0.5" | "1" | "1.5" | "2">("1");
  const [actualDuration, setActualDuration] = useState<number | null>(null);
  const wave = useMemo(() => bars(call.executionId), [call.executionId]);
  const signedUrl = useRecordingUrl(call.recordingUrl, call.callId);
  
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = parseFloat(speed);
    }
  }, [speed]);

  useEffect(() => {
    if (signedUrl) {
      if (!audioRef.current) {
        audioRef.current = new Audio(signedUrl);
        audioRef.current.addEventListener('timeupdate', () => {
          if (audioRef.current) {
            setElapsed(audioRef.current.currentTime);
          }
        });
        audioRef.current.addEventListener('loadedmetadata', () => {
          if (audioRef.current && isFinite(audioRef.current.duration)) {
            setActualDuration(audioRef.current.duration);
          }
        });
        audioRef.current.addEventListener('ended', () => {
          setPlaying(false);
        });
      } else if (audioRef.current.src !== signedUrl) {
        audioRef.current.src = signedUrl;
        setActualDuration(null);
      }
      
      if (playing) {
        audioRef.current.play().catch(e => console.error("Audio play failed:", e));
      } else {
        audioRef.current.pause();
      }
    } else {
      // Simulation mode
      if (!playing) return;
      const t = window.setInterval(
        () => setElapsed((e) => Math.min(e + 0.25 * Number(speed), actualDuration ?? call.durationSec)),
        250,
      );
      return () => window.clearInterval(t);
    }
  }, [playing, speed, call.durationSec, signedUrl, call.recordingUrl, actualDuration]);

  const displayDuration = actualDuration ?? call.durationSec;

  useEffect(() => {
    if (!call.recordingUrl && elapsed >= displayDuration) {
      setPlaying(false);
    }
  }, [elapsed, displayDuration, call.recordingUrl]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const progress = displayDuration ? elapsed / displayDuration : 0;

  return (
    <Modal
      title="Call Recording"
      subtitle={<span className="font-mono text-xs">{call.executionId} · {call.contactId}</span>}
      onClose={onClose}
      width="max-w-3xl"
      headerSlot={
        <div className="border-b border-line px-5 pb-5 pt-5">
          <div className="rounded-xl border border-line bg-soft p-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setPlaying((v) => !v)}
                aria-label={playing ? "Pause" : "Play"}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand text-white transition-colors hover:bg-brand-hover shadow-xs"
              >
                {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 pl-0.5" />}
              </button>

              {/* Waveform (click to seek) */}
              <div
                role="slider"
                aria-label="Seek"
                aria-valuenow={Math.round(elapsed)}
                aria-valuemin={0}
                aria-valuemax={displayDuration}
                className="flex h-12 flex-1 cursor-pointer items-center gap-[2px]"
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const ratio = (e.clientX - rect.left) / rect.width;
                  const newElapsed = ratio * displayDuration;
                  setElapsed(newElapsed);
                  if (audioRef.current) {
                    audioRef.current.currentTime = newElapsed;
                  }
                }}
              >
                {wave.map((h, i) => (
                  <span
                    key={i}
                    style={{ height: `${h * 100}%` }}
                    className={cn(
                      "flex-1 rounded-full",
                      i / wave.length <= progress ? "bg-brand" : "bg-line",
                    )}
                  />
                ))}
              </div>

              <span className="shrink-0 font-mono text-xs text-ink-2">
                {formatDuration(elapsed)} / {formatDuration(displayDuration)}
              </span>

              <Dropdown ariaLabel="Playback speed" className="w-20" value={speed} options={SPEEDS} onChange={setSpeed as any} />
            </div>
          </div>
        </div>
      }
    >
      {/* AI Summary */}
      <div className="rounded-lg bg-soft p-4">
        <p className="mb-2 font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">AI Summary</p>
        <ul className="space-y-1.5">
          {call.aiSummary.map((s, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-ink-2">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" /> {s}
            </li>
          ))}
        </ul>
      </div>

      {/* Transcript */}
      <div className="mt-5">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-3">Transcript</p>
        <TranscriptConversation call={call} className="mt-3" />
      </div>
    </Modal>
  );
}
